/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dimensions, StyleSheet, View, TouchableOpacity } from 'react-native';
import Toast from 'react-native-toast-message';

import ModalFull from '@components/modal/ModalFull';
import { FormGroup, KeyboardWrapper } from '@components/screen/Layout';
import SelectBox from '@components/form/SelectBox';
import TitleSubSection from '@components/global/TitleSubSection';
import FileInput from '@components/form/FileInput';
import SociollaText from '@components/sociolla/SociollaText';
import Button from '@components/form/Button';

import Colors from '@global/colors';
import { showPopup } from '@/store/modal';
import { isWeb } from '@global/screens';
import { MsOrder } from '@api/ms-order';
import { useDispatch } from 'react-redux';

const propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  data: PropTypes.object,
  isAdd: PropTypes.bool,
  handleSubmitPeriod: PropTypes.func,
};

const dimension = Dimensions.get('screen');

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  const gapYear = 3;
  for (let i = currentYear + gapYear; i >= currentYear - gapYear; i--) {
    years.push({ label: i.toString(), value: i });
  }
  return years;
};

const yearOptions = generateYearOptions();

const ModalAddEditPeriod = (props) => {
  const dispatch = useDispatch();
  const [attachments, setAttachments] = useState([
    { id: 1, name: null, url: null },
  ]);
  const [year, setYear] = useState();

  useEffect(() => {
    if (!props.isAdd && props.data) {
      // Set year from data
      if (props.data.year) {
        const yearOption = yearOptions.find(
          (opt) => opt.value === props.data.year,
        );
        setYear(
          yearOption || {
            label: props.data.year.toString(),
            value: props.data.year,
          },
        );
      }

      // Set attachments from data
      if (props.data.attachments && props.data.attachments.length > 0) {
        const formattedAttachments = props.data.attachments.map(
          (attachment, index) => ({
            id: attachment.id || index + 1,
            name: attachment.name,
            url: attachment.url,
          }),
        );
        setAttachments(formattedAttachments);
      }
    }
  }, [props.isAdd, props.data]);

  const handleAddAttachment = () => {
    const newId =
      attachments.length > 0
        ? Math.max(...attachments.map((a) => a.id)) + 1
        : 1;
    setAttachments([...attachments, { id: newId, name: null, url: null }]);
  };

  const handleRemoveAttachment = (id) => {
    if (attachments.length > 1) {
      setAttachments(attachments.filter((a) => a.id !== id));
    }
  };

  const handleAttachmentChange = (id, value) => {
    setAttachments(
      attachments.map((a) =>
        a.id === id ? { ...a, name: value.name, url: value.url } : a,
      ),
    );
  };

  const handleCloseModal = () => {
    setYear();
    setAttachments([{ id: 1, name: null, url: null }]);
    props.onClose();
  };

  const handleSubmit = async () => {
    try {
      if (!year) {
        Toast.show({
          text1: 'Period is required',
          type: 'error',
        });
        return;
      }

      // Check for attachments without uploaded files
      const missingAttachments = attachments
        .map((attachment, index) => (!attachment.url ? index + 1 : null))
        .filter((index) => index !== null);

      if (missingAttachments.length > 0) {
        const attachmentLabels = missingAttachments
          .map((num) => `Attachment ${num}`)
          .join(', ');
        Toast.show({
          text1: `${attachmentLabels} ${
            missingAttachments.length > 1 ? 'are' : 'is'
          } required`,
          type: 'error',
        });
        return;
      }

      dispatch(showPopup({ name: 'isLoading', value: true }));

      const finalPayload = {
        year: year.value,
        attachments,
      };

      if (props.isAdd) {
        await MsOrder.createRequestProductPeriod(finalPayload);
      } else {
        await MsOrder.updateRequestProductPeriod(props.data.id, finalPayload);
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
      await props.handleSubmitPeriod();
    } catch (error) {
      console.log(error);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  return (
    <ModalFull
      show={props.show}
      title={`${props.isAdd ? 'Add' : 'Edit'} Periode`}
      width={isWeb ? 650 : '100%'}
      onClose={handleCloseModal}>
      <KeyboardWrapper
        style={
          isWeb
            ? { maxHeight: dimension.height - 300 }
            : { maxHeight: dimension.height - 50 }
        }
        keyboardVerticalOffset={100}
        contentContainerStyle={{
          width: '100%',
          padding: 15,
        }}>
        <FormGroup>
          <SelectBox
            label="Periode"
            options={yearOptions}
            value={year}
            placeholder="Select Periode"
            trackBy="value"
            labelKey="label"
            isRequired={true}
            onChange={(value) => {
              setYear(value);
            }}
            hideSearch={true}
          />
        </FormGroup>

        <TitleSubSection
          title={'ATTACHMENTS'}
          button={{
            label: 'Add More',
            callback: handleAddAttachment,
          }}
          style={{ marginBottom: 10 }}
        />
        {attachments.map((attachment, index) => (
          <FormGroup key={attachment.id}>
            <View style={styles.attachmentRow}>
              <View style={styles.fileInputContainer}>
                <FileInput
                  confirmRemove
                  isRequired
                  label={`Attachment ${index + 1}`}
                  value={attachment.name}
                  onChange={(value) =>
                    handleAttachmentChange(attachment.id, value)
                  }
                  withFileName
                  containerStyleExtra={styles.uploadForm}
                />
              </View>
              {attachments.length > 1 && (
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={() => handleRemoveAttachment(attachment.id)}>
                  <SociollaText style={styles.removeButtonText}>
                    Remove
                  </SociollaText>
                </TouchableOpacity>
              )}
            </View>
          </FormGroup>
        ))}

        <View style={styles.btnContainer}>
          <View style={styles.buttonWrapper}>
            <Button
              type="default"
              rounded={true}
              buttonStyle={styles.button}
              title={
                <SociollaText type="medium" style={{ fontSize: 14 }}>
                  Cancel
                </SociollaText>
              }
              onPress={handleCloseModal}
            />
          </View>
          <View style={styles.buttonWrapper}>
            <Button
              type="primary"
              rounded={true}
              buttonStyle={styles.button}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  {props.isAdd ? 'Add' : 'Update'}
                </SociollaText>
              }
              onPress={handleSubmit}
            />
          </View>
        </View>
      </KeyboardWrapper>
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  attachmentRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    width: '100%',
  },
  fileInputContainer: {
    flex: 1,
    marginRight: 10,
  },
  removeButton: {
    paddingVertical: 5,
    paddingHorizontal: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.grey0,
  },
  removeButtonText: {
    color: Colors.grey1,
  },
  btnContainer: {
    flexDirection: 'row',
    marginTop: 20,
    alignItems: 'center',
    gap: 10,
  },
  buttonWrapper: {
    flex: 1,
  },
  button: {
    width: '100%',
  },
  uploadForm: {
    borderRadius: 4,
    borderWidth: 1,
    height: isWeb ? 35 : 45,
    paddingHorizontal: 16,
    borderColor: Colors.grey0,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

ModalAddEditPeriod.propTypes = propTypes;

export default ModalAddEditPeriod;
